import { Vec2 } from '@lakaran/geometry';
import type { CadEditor } from '../src/editor/editor';

/**
 * Picking whole pieces of a step's stream in the drawing.
 *
 * Two steps work this way — trim removes spans, mirror reflects a chosen set — and both are
 * non-destructive: the overlay only paints the stream, and a click writes a key onto the node.
 * This module is that machinery; `trim-picker` and `mirror-picker` give it their own state.
 */

export interface PickPiece {
  key: string;
  label: string;
  /** True when the piece is in the state the overlay paints as "off" (removed, not picked). */
  on: boolean;
  /** Flattened world polylines of the piece. */
  paths: Array<{ points: Vec2[]; closed: boolean }>;
}

export interface PiecePickerOptions {
  /** The pieces of one step, recomputed after every toggle. */
  pieces: (nodeId: string) => PickPiece[];
  /** Flips one piece; `on` is the state it should switch to. */
  onToggle: (nodeId: string, key: string, on: boolean) => void;
  /** Line shown above the drawing while the mode is armed. */
  hint: string;
  startMessage: (count: number) => string;
  emptyMessage: string;
  /** Label under the pointer, e.g. "click to trim". */
  action: (piece: PickPiece) => string;
  /** Class painted on pieces that are `on`. */
  onClass: string;
  /** Extra overlay content, drawn each repaint (the mirror axis, for instance). */
  decorate?: (nodeId: string, svg: SVGSVGElement) => void;
  onEnd?: () => void;
}

export interface PiecePicker {
  start(nodeId: string): void;
  end(): void;
  get active(): boolean;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
/** Screen-pixel reach of the pointer over a piece. */
const REACH_PX = 9;

export function createPiecePicker(
  editor: CadEditor,
  host: HTMLElement,
  options: PiecePickerOptions,
): PiecePicker {
  const layer = document.createElement('div');
  layer.className = 'trim-pick-layer';
  layer.hidden = true;

  const svg = document.createElementNS(SVG_NS, 'svg') as SVGSVGElement;
  svg.classList.add('trim-pick-svg');
  const hint = document.createElement('div');
  hint.className = 'ref-pick-hint';
  const label = document.createElement('div');
  label.className = 'ref-pick-label';
  label.hidden = true;
  layer.append(svg, label, hint);
  host.append(layer);

  const canvas = host.querySelector('canvas');
  let session: { nodeId: string; pieces: PickPiece[]; hovered: PickPiece | null } | null = null;
  let unlistenView: (() => void) | null = null;

  const toScreen = (point: Vec2): Vec2 => editor.viewport.worldToScreen(point);

  const draw = (): void => {
    if (!session) return;
    svg.setAttribute('width', String(editor.viewport.width));
    svg.setAttribute('height', String(editor.viewport.height));
    svg.replaceChildren();

    for (const piece of session.pieces) {
      for (const path of piece.paths) {
        if (path.points.length < 2) continue;
        const points = path.points
          .map((point) => {
            const screen = toScreen(point);
            return `${screen.x.toFixed(1)},${screen.y.toFixed(1)}`;
          })
          .join(' ');
        const shape = document.createElementNS(SVG_NS, 'polyline');
        shape.setAttribute('points', points);
        shape.setAttribute('class', 'trim-piece');
        if (piece.on) shape.classList.add(options.onClass);
        if (piece === session.hovered) shape.classList.add('is-hot');
        svg.append(shape);
      }
    }

    options.decorate?.(session.nodeId, svg);

    if (!session.hovered) {
      label.hidden = true;
      return;
    }
    const first = session.hovered.paths[0]?.points[0];
    if (!first) {
      label.hidden = true;
      return;
    }
    const screen = toScreen(first);
    label.hidden = false;
    label.textContent = `${session.hovered.label} · ${options.action(session.hovered)}`;
    label.style.left = `${screen.x}px`;
    label.style.top = `${screen.y}px`;
    label.classList.toggle('is-left', screen.x > editor.viewport.width - 200);
    label.classList.toggle('is-restore', !session.hovered.on);
  };

  const nearest = (local: Vec2): PickPiece | null => {
    if (!session) return null;
    const world = editor.viewport.screenToWorld(local);
    const reach = REACH_PX / Math.max(editor.viewport.scale, 1e-9);
    let best: { piece: PickPiece; distance: number } | null = null;
    for (const piece of session.pieces) {
      for (const path of piece.paths) {
        for (let index = 0; index + 1 < path.points.length; index++) {
          const distance = distanceToSegment(world, path.points[index]!, path.points[index + 1]!);
          if (distance > reach || (best && distance >= best.distance)) continue;
          best = { piece, distance };
        }
      }
    }
    return best?.piece ?? null;
  };

  const toLocal = (event: PointerEvent | MouseEvent): Vec2 => {
    const rect = canvas?.getBoundingClientRect();
    return Vec2.of(event.clientX - (rect?.left ?? 0), event.clientY - (rect?.top ?? 0));
  };

  const onMove = (event: PointerEvent): void => {
    if (!session) return;
    event.stopPropagation();
    const hovered = nearest(toLocal(event));
    if (hovered === session.hovered) return;
    session.hovered = hovered;
    draw();
  };

  const onDown = (event: PointerEvent): void => {
    if (!session) return;
    event.stopPropagation();
    event.preventDefault();
    if (event.button !== 0 || !session.hovered) return;
    const piece = session.hovered;
    const nodeId = session.nodeId;
    options.onToggle(nodeId, piece.key, !piece.on);
    // A one-shot picker ends itself inside `onToggle`; nothing left to rebuild.
    if (!session) return;
    // The stream changed: rebuild the pieces and keep picking until the user ends it.
    session.pieces = options.pieces(nodeId);
    session.hovered = session.pieces.find((candidate) => candidate.key === piece.key) ?? null;
    draw();
  };

  const onContext = (event: MouseEvent): void => {
    if (!session) return;
    event.preventDefault();
    event.stopPropagation();
    end();
  };

  const onKey = (event: KeyboardEvent): void => {
    if (!session) return;
    const key = event.key.toLowerCase();
    if (key !== 'escape' && key !== 'x') return;
    event.preventDefault();
    event.stopPropagation();
    end();
  };

  const onView = (): void => draw();

  function end(): void {
    if (!session) return;
    session = null;
    layer.hidden = true;
    svg.replaceChildren();
    label.hidden = true;
    hint.textContent = '';
    unlistenView?.();
    unlistenView = null;
    canvas?.removeEventListener('pointermove', onMove, true);
    canvas?.removeEventListener('pointerdown', onDown, true);
    canvas?.removeEventListener('contextmenu', onContext, true);
    window.removeEventListener('keydown', onKey, true);
    canvas?.classList.remove('is-picking');
    options.onEnd?.();
  }

  const start = (nodeId: string): void => {
    end();
    const pieces = options.pieces(nodeId);
    if (pieces.length === 0) {
      editor.message(options.emptyMessage);
      return;
    }

    session = { nodeId, pieces, hovered: null };
    layer.hidden = false;
    hint.textContent = options.hint;
    canvas?.classList.add('is-picking');
    unlistenView = editor.events.on('view', onView);
    canvas?.addEventListener('pointermove', onMove, true);
    canvas?.addEventListener('pointerdown', onDown, true);
    canvas?.addEventListener('contextmenu', onContext, true);
    window.addEventListener('keydown', onKey, true);
    draw();
    editor.message(options.startMessage(pieces.length));
  };

  return {
    start,
    end,
    get active() {
      return session !== null;
    },
  };
}

export interface PieceHighlighter {
  /** Light up pieces of one step; call with `null` to clear. */
  show(nodeId: string, keys: readonly string[] | null): void;
  dispose(): void;
}

/**
 * A lightweight version of the pick overlay used by card lists: hovering a row lights up the
 * pieces it names without arming the picker.
 */
export function createPieceHighlighter(
  editor: CadEditor,
  host: HTMLElement,
  options: {
    pieces: (nodeId: string) => PickPiece[];
    /** Class painted on the lit pieces. */
    hotClass: string;
  },
): PieceHighlighter {
  const layer = document.createElement('div');
  layer.className = 'trim-hover-layer';
  layer.hidden = true;
  const svg = document.createElementNS(SVG_NS, 'svg') as SVGSVGElement;
  svg.classList.add('trim-pick-svg');
  layer.append(svg);
  host.append(layer);

  let current: { nodeId: string; keys: string[] } | null = null;
  let unlisten: (() => void) | null = null;

  const draw = (): void => {
    if (!current) return;
    svg.setAttribute('width', String(editor.viewport.width));
    svg.setAttribute('height', String(editor.viewport.height));
    svg.replaceChildren();
    const wanted = new Set(current.keys);
    for (const piece of options.pieces(current.nodeId)) {
      if (!wanted.has(piece.key)) continue;
      for (const path of piece.paths) {
        if (path.points.length < 2) continue;
        const shape = document.createElementNS(SVG_NS, 'polyline');
        shape.setAttribute(
          'points',
          path.points
            .map((point) => {
              const screen = editor.viewport.worldToScreen(point);
              return `${screen.x.toFixed(1)},${screen.y.toFixed(1)}`;
            })
            .join(' '),
        );
        shape.setAttribute('class', `trim-piece is-hot ${options.hotClass}`);
        svg.append(shape);
      }
    }
  };

  const onView = (): void => draw();

  const show = (nodeId: string, keys: readonly string[] | null): void => {
    if (!keys || keys.length === 0) {
      current = null;
      layer.hidden = true;
      svg.replaceChildren();
      unlisten?.();
      unlisten = null;
      return;
    }
    current = { nodeId, keys: [...keys] };
    layer.hidden = false;
    unlisten ??= editor.events.on('view', onView);
    draw();
  };

  return {
    show,
    dispose() {
      unlisten?.();
      unlisten = null;
      layer.remove();
    },
  };
}

function distanceToSegment(point: Vec2, a: Vec2, b: Vec2): number {
  const ab = b.sub(a);
  const lengthSq = ab.dot(ab);
  if (lengthSq < 1e-12) return point.distanceTo(a);
  const t = Math.max(0, Math.min(1, point.sub(a).dot(ab) / lengthSq));
  return point.distanceTo(a.add(ab.scale(t)));
}
